import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2, GripVertical, Crown, Loader2, Library, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * Left pane of the 3-column ThesisWorkspace.
 *
 * Pure prop-driven component. All state lives in ThesisWorkspace.js — this
 * file is presentation + tiny inline handlers for the indent/outdent and
 * promote/demote buttons that need direct API access.
 *
 * Extracted from ThesisWorkspace.js (Sprint 6 refactor) — was inline at
 * 2300 lines, now lives in its own ~140-line module.
 */
export default function ChaptersListPanel({
  chapters,
  docs,
  userId,
  openChapter,
  draggedChapterId,
  dragOverChapterId,
  dragOverMode,
  onSelectChapter,         // (chId) => void
  onDeleteChapter,         // (chId, title) => void
  onChapterDragStart,
  onChapterDragOver,
  onChapterDragLeave,
  onChapterDragEnd,
  onChapterDrop,
  onOpenChapterLibrary,    // (chapter) => void
  onOpenGenerateDialog,    // (chapter) => void
  onHierarchyChanged,      // () => void   (parent triggers loadAll)
}) {
  return (
    <div className="space-y-2">
      {chapters.map((c) => {
        const attachedCount = docs.filter((d) => (d.chapter_ids || []).includes(c.id)).length;
        const isOutlineOnly = !c.markdown || c.markdown.length < 200;
        const depth = c.depth || 0;
        const isDragging = draggedChapterId === c.id;
        const isDropTarget = dragOverChapterId === c.id && draggedChapterId !== c.id;
        const dropBorderClass = isDropTarget
          ? dragOverMode === 'above' ? 'border-t-4 border-t-blue-500'
          : dragOverMode === 'below' ? 'border-b-4 border-b-blue-500'
          : dragOverMode === 'into' ? 'ring-2 ring-blue-500 bg-blue-50/30 dark:bg-blue-950/20' : ''
          : '';

        // Find a top-level chapter immediately above this one to use as the
        // default parent when the user clicks "Sub-section".
        let parentCandidate = null;
        if (depth === 0) {
          const idx = chapters.findIndex((x) => x.id === c.id);
          parentCandidate = idx > 0
            ? [...chapters].slice(0, idx).reverse().find((x) => (x.depth || 0) === 0)
            : null;
        }

        const handlePromote = async (e) => {
          e.stopPropagation();
          try {
            await thesisApi.setChapterHierarchy(userId, c.id, null);
            toast.success('Promoted to top-level chapter');
            onHierarchyChanged?.();
          } catch (err) {
            toast.error(`Promote failed: ${err.message}`);
          }
        };

        const handleDemote = async (e) => {
          if (!parentCandidate) return;
          e.stopPropagation();
          try {
            await thesisApi.setChapterHierarchy(userId, c.id, parentCandidate.id);
            toast.success(`Nested under "${parentCandidate.title}"`);
            onHierarchyChanged?.();
          } catch (err) {
            toast.error(`Demote failed: ${err.message}`);
          }
        };

        return (
          <Card key={c.id}
            draggable
            onDragStart={(e) => onChapterDragStart(e, c.id)}
            onDragOver={(e) => onChapterDragOver(e, c.id, depth)}
            onDragLeave={onChapterDragLeave}
            onDragEnd={onChapterDragEnd}
            onDrop={(e) => onChapterDrop(e, c.id, depth, c.parent_id || null)}
            style={{ marginLeft: depth * 16, opacity: isDragging ? 0.4 : 1 }}
            className={`cursor-pointer hover:shadow-md transition ${openChapter?.id === c.id ? 'ring-2 ring-primary' : ''} ${c.premium_status === 'running' ? 'border-amber-600/40 bg-amber-500/[0.03]' : ''} ${depth > 0 ? 'border-l-2 border-l-blue-300 dark:border-l-blue-700' : ''} ${dropBorderClass}`}
            onClick={() => onSelectChapter(c.id)}
            data-testid={`chapter-item-${c.id}`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className={`font-medium leading-snug flex items-center gap-1.5 ${depth > 0 ? 'text-xs' : 'text-sm'}`}>
                  <GripVertical className="w-3 h-3 text-slate-300 dark:text-slate-600 cursor-grab active:cursor-grabbing flex-shrink-0"
                    title="Drag to reorder · drop on a chapter to nest as sub-section" />
                  {c.premium_engine && (
                    <Crown className={`w-3 h-3 shrink-0 ${c.premium_status === 'ready' ? 'text-amber-600' : c.premium_status === 'failed' ? 'text-red-500' : 'text-amber-500 animate-pulse'}`} />
                  )}
                  {typeof c.order === 'number' && (
                    <span className="text-[10px] text-muted-foreground font-mono">
                      {depth > 0 ? '↳' : ''} {c.order + 1}.
                    </span>
                  )}
                  {c.title}
                </h3>
                <Button variant="ghost" size="icon" className="h-6 w-6"
                  onClick={(e) => { e.stopPropagation(); onDeleteChapter(c.id, c.title); }}>
                  <Trash2 className="w-3 h-3 text-destructive" />
                </Button>
              </div>
              {c.description && (
                <p className="text-[11px] text-muted-foreground/80 mt-1 line-clamp-2 italic">{c.description}</p>
              )}
              {c.premium_status === 'running' ? (
                <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-1 flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {c.premium_progress || 'Generating…'}
                </p>
              ) : c.premium_status === 'failed' ? (
                <p className="text-[11px] text-red-600 dark:text-red-400 mt-1 line-clamp-2" title={c.premium_error}>
                  ⚠ Failed — {c.premium_error?.slice(0, 80) || 'unknown error'}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  {c.citations?.length || 0} citations · {c.markdown?.length || 0} chars
                </p>
              )}
              <div className="mt-2 flex items-center gap-1 flex-wrap">
                <button type="button"
                  className={`text-[10px] px-1.5 py-0.5 rounded-full border transition ${attachedCount > 0 ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900' : 'border-slate-200 bg-slate-50 text-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-700'} hover:border-blue-400`}
                  onClick={(e) => { e.stopPropagation(); onOpenChapterLibrary(c); }}
                  data-testid={`chapter-library-btn-${c.id}`}
                  title="Manage this chapter's evidence library">
                  <Library className="w-3 h-3 inline mr-0.5" />Library · {attachedCount}
                </button>
                {isOutlineOnly && c.premium_status !== 'running' && (
                  <button type="button"
                    className="text-[10px] px-1.5 py-0.5 rounded-full border border-blue-300 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900 hover:border-blue-500"
                    onClick={(e) => { e.stopPropagation(); onOpenGenerateDialog(c); }}
                    data-testid={`chapter-generate-btn-${c.id}`}
                    title="Generate content for this chapter (scoped to its library)">
                    <Sparkles className="w-3 h-3 inline mr-0.5" />Write
                  </button>
                )}
                {depth > 0 ? (
                  <button type="button"
                    className="text-[10px] px-1.5 py-0.5 rounded-full border border-slate-300 dark:border-slate-700 hover:border-blue-400 text-slate-600 dark:text-slate-400"
                    onClick={handlePromote}
                    data-testid={`chapter-promote-btn-${c.id}`}
                    title="Promote to top-level chapter">
                    ⬆ Promote
                  </button>
                ) : parentCandidate ? (
                  <button type="button"
                    className="text-[10px] px-1.5 py-0.5 rounded-full border border-slate-300 dark:border-slate-700 hover:border-blue-400 text-slate-600 dark:text-slate-400"
                    onClick={handleDemote}
                    data-testid={`chapter-demote-btn-${c.id}`}
                    title={`Make sub-section of "${parentCandidate.title}"`}>
                    ⬇ Sub-section
                  </button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
